/**
 * Author: Agent
 * Generation: 前端日志抓取器（结构化客户端轨迹）
 * Version: v3.2.227
 * Modified: 2025-11-08
 * One-line: 采集关键前端事件（接收/加载/渲染/回放），低噪音、可下载导出
 */

export type TracePhase = 'receive' | 'load' | 'render' | 'playback' | 'ws' | 'ui' | 'auth';
export type TraceModule = 'ws-bridge' | 'ws-core' | 'gamepage' | 'battlescene' | 'playback' | 'animation' | 'preload' | 'auth';

export interface TraceEntry {
  ts: string; // ISO
  t: number;  // epoch ms（便于排序）
  phase: TracePhase;
  module: TraceModule;
  event: string; // 事件名（统一大写或业务名）
  roomId?: string;
  turn?: number;
  agentId?: string;
  details?: any; // 关键信息（轻量）
}

type TurnSupplier = () => number | undefined | null;
type RoomSupplier = () => string | undefined | null;

class ClientTraceImpl {
  private enabled: boolean;
  private buffer: TraceEntry[] = [];
  private maxEntries = 5000;
  private getTurn: TurnSupplier | null = null;
  private getRoom: RoomSupplier | null = null;
  // 自动持久化/导出
  private autoFlushMs = 0; // 默认不启用周期刷盘
  private flushTimer: number | null = null;
  private lastFlushed = 0;
  private autoExport = false; // 默认不进行周期自动下载
  private storageIndexKey = 'ABR_TRACE_INDEX';
  private storageChunkPrefix = 'ABR_TRACE_CHUNK_';
  private downloadOnExit = true; // 退出时尝试整包导出一次
  // 可注入的后端上传器（由 WebSocketService 在连接成功后注入）
  private uploader: ((filename: string, content: string) => Promise<boolean>) | null = null;

  constructor() {
    // 开关策略：默认开启；可通过以下方式关闭
    // 关闭优先级：env VITE_CLIENT_TRACE_DISABLED=1 > query ?trace=0 > localStorage ABR_TRACE=0
    const envDisabled = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_CLIENT_TRACE_DISABLED) ?? '';
    const envEnabled = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_CLIENT_TRACE_ENABLED) ?? '';
    const qp = typeof window !== 'undefined' ? (new URLSearchParams(window.location.search).get('trace')) : null;
    const ls = typeof window !== 'undefined' ? window.localStorage.getItem('ABR_TRACE') : null;
    let flag = true;
    if (String(envDisabled) === '1' || String(envDisabled).toLowerCase() === 'true') flag = false;
    if (qp === '0' || qp === 'false') flag = false;
    if (ls === '0' || ls === 'false') flag = false;
    if (String(envEnabled) === '1' || String(envEnabled).toLowerCase() === 'true') flag = true;
    this.enabled = flag;
    // 自动导出开关（仅在退出时，默认开启；周期性导出关闭）
    try {
      const a = window.localStorage.getItem('ABR_TRACE_AUTOEXPORT');
      if (a === '0' || a === 'false') this.downloadOnExit = false;
    } catch {}
    // 暴露给 window 便于手动导出/开启
    try {
      (window as any).__ABR_TRACE__ = {
        enable: () => { this.enabled = true; try { window.localStorage.setItem('ABR_TRACE', '1'); } catch {} },
        disable: () => { this.enabled = false; try { window.localStorage.removeItem('ABR_TRACE'); } catch {} },
        download: () => this.download(),
        clear: () => this.clear(),
        size: () => this.buffer.length,
        peek: (n = 20) => this.buffer.slice(-n),
        startAuto: (ms?: number) => this.startAutoFlush(ms),
        stopAuto: () => this.stopAutoFlush(),
        flush: (reason?: string) => this.flushToStorage(reason || 'manual'),
        exportAll: () => this.exportAllNow(),
      };
    } catch {}
  }

  setTurnSupplier(fn: TurnSupplier) { this.getTurn = fn; }
  setRoomSupplier(fn: RoomSupplier) { this.getRoom = fn; }
  setEnabled(flag: boolean) { this.enabled = !!flag; }
  isEnabled() { return !!this.enabled; }

  log(phase: TracePhase, module: TraceModule, event: string, payload?: { turn?: number; agentId?: string; roomId?: string; details?: any }): void {
    if (!this.enabled) return;
    try {
      const now = Date.now();
      const entry: TraceEntry = {
        ts: new Date(now).toISOString(),
        t: now,
        phase,
        module,
        event,
        roomId: payload?.roomId ?? this.safeRoom(),
        turn: payload?.turn ?? this.safeTurn(),
        agentId: payload?.agentId,
        details: payload?.details,
      };
      this.buffer.push(entry);
      if (this.buffer.length > this.maxEntries) {
        this.buffer.splice(0, this.buffer.length - this.maxEntries);
      }
    } catch {}
  }

  private safeTurn(): number | undefined {
    try { return this.getTurn ? (this.getTurn() ?? undefined) : undefined; } catch { return undefined; }
  }
  private safeRoom(): string | undefined {
    try { return this.getRoom ? (this.getRoom() ?? undefined) : undefined; } catch { return undefined; }
  }

  clear(): void { this.buffer = []; }

  // 由 WebSocketService 注入上传函数
  setUploader(fn: (filename: string, content: string) => Promise<boolean>) { this.uploader = fn; }

  // 组装完整 NDJSON 文本（已持久化分片 + 末尾内存差量）
  private buildFullNdjson(): string {
    const chunks = this.collectAllChunks();
    const tail = this.buffer.slice(this.lastFlushed).map(e => JSON.stringify(e));
    return [...chunks, ...tail].join('\n');
  }

  // 格式化中国时区时间，用于文件名（YYYY-MM-DDTHH-mm-ss+08-00）
  private formatCN(now: number): string {
    try {
      const dt = new Date(now);
      const parts = new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai',
        hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      }).formatToParts(dt).reduce((acc: any, p) => { acc[p.type] = p.value; return acc; }, {});
      return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}-${parts.minute}-${parts.second}+08-00`;
    } catch {
      return new Date(now).toISOString().replace(/[:.]/g,'-');
    }
  }

  async download(filename?: string): Promise<void> {
    if (!this.enabled || typeof window === 'undefined') return;
    try {
      let rid = this.safeRoom() || undefined;
      try { if (!rid) rid = (window as any).__ABR_CURRENT_ROOM_ID__ || undefined; } catch {}
      const name = filename || `client_trace_${rid || 'unknown'}_${this.formatCN(Date.now())}.ndjson`;
      const ndjson = this.buildFullNdjson();

      // 优先尝试通过后端上传（保存到 backend/logs/client_traces）
      if (this.uploader) {
        try {
          const ok = await this.uploader(name, ndjson);
          if (ok) return; // 上传成功则不触发浏览器下载
        } catch {}
      }

      // 回退：浏览器本地下载到默认下载目录
      const blob = new Blob([ndjson], { type: 'application/x-ndjson' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {}
  }

  // ================= 自动持久化 / 导出 =================
  startAutoFlush(ms?: number): void {
    // 为兼容API保留，但默认不启用周期导出；仅注册生命周期 flush
    if (typeof window === 'undefined') return;
    if (ms) this.autoFlushMs = ms;
    this.stopAutoFlush();
    try {
      window.addEventListener('visibilitychange', () => {
        if ((document as any).visibilityState === 'hidden') {
          this.flushToStorage('visibilitychange');
          if (this.downloadOnExit) this.exportAllNow(/*fallbackOnly*/ true);
        }
      });
      window.addEventListener('pagehide', () => {
        this.flushToStorage('pagehide');
        if (this.downloadOnExit) this.exportAllNow(true);
      });
      window.addEventListener('beforeunload', () => {
        this.flushToStorage('beforeunload');
        if (this.downloadOnExit) this.exportAllNow(true);
      });
    } catch {}
    // 不启动定时器（避免周期生成文件）
    this.flushTimer = null;
  }
  stopAutoFlush(): void { if (this.flushTimer) { try { clearInterval(this.flushTimer); } catch {}; this.flushTimer = null; } }

  private flushToStorage(reason: string): void {
    if (!this.enabled || typeof window === 'undefined') return;
    try {
      if (this.lastFlushed >= this.buffer.length) return; // 无新数据
      const slice = this.buffer.slice(this.lastFlushed);
      if (!slice.length) return;
      const ndjson = slice.map(e => JSON.stringify(e)).join('\n');
      const key = `${this.storageChunkPrefix}${Date.now()}`;
      window.localStorage.setItem(key, ndjson);
      const idxRaw = window.localStorage.getItem(this.storageIndexKey);
      const idx: string[] = idxRaw ? JSON.parse(idxRaw) : [];
      idx.push(key);
      window.localStorage.setItem(this.storageIndexKey, JSON.stringify(idx));
      this.lastFlushed = this.buffer.length;
      // 周期自动下载关闭；仅在退出事件中尝试整包导出
    } catch {}
  }

  // 公共导出：用于在“游戏结束”等场景主动生成日志
  async exportNow(reason?: string): Promise<void> {
    try { this.flushToStorage(reason || 'exportNow'); } catch {}
    try { await this.download(); } catch {}
  }

  private collectAllChunks(): string[] {
    try {
      const idxRaw = window.localStorage.getItem(this.storageIndexKey);
      const idx: string[] = idxRaw ? JSON.parse(idxRaw) : [];
      const res: string[] = [];
      idx.forEach(k => { const v = window.localStorage.getItem(k); if (v) res.push(v); });
      return res;
    } catch { return []; }
  }

  private downloadChunk(chunkKey: string): void {
    try {
      const rid = this.safeRoom() || 'unknown';
      const content = window.localStorage.getItem(chunkKey) || '';
      if (!content) return;
      const blob = new Blob([content], { type: 'application/x-ndjson' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${chunkKey}_${rid}.ndjson`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch {}
  }

  installAutoHooks(ms?: number): void {
    this.startAutoFlush(ms);
    // 如上次遗留分片或标记存在，进入页面后合并导出一次并清理
    try {
      const idxRaw = window.localStorage.getItem(this.storageIndexKey);
      const idx: string[] = idxRaw ? JSON.parse(idxRaw) : [];
      if (idx.length || window.localStorage.getItem('ABR_TRACE_PENDING') === '1') {
        this.download();
        idx.forEach(k => window.localStorage.removeItem(k));
        window.localStorage.removeItem(this.storageIndexKey);
        window.localStorage.removeItem('ABR_TRACE_PENDING');
      }
    } catch {}
  }

  // 在退出时尝试整包导出；某些浏览器会拦截下载，失败则设置待导出标记
  private exportAllNow(fallbackOnly = false): void {
    try {
      if (!this.enabled || typeof window === 'undefined') return;
      // 将当前内存尾部先flush到本地，确保全量
      this.flushToStorage('exportAll');
      const perform = async () => {
        try { await this.download(); } catch { window.localStorage.setItem('ABR_TRACE_PENDING','1'); }
      };
      if (fallbackOnly) { perform(); } else { perform(); }
    } catch {
      try { window.localStorage.setItem('ABR_TRACE_PENDING','1'); } catch {}
    }
  }
}

export const ClientTrace = new ClientTraceImpl();
export default ClientTrace;
